import React, { Dispatch, FC, Fragment, SetStateAction } from 'react';
import { SearchOutlined } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import { DeviceSearchResponse } from 'api/Responses/SearchResponses';
import Theme from 'App.theme';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import InputField from 'components/InputField';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import useQueryParts from 'hooks/useQueryParts';
import { PartAction, partActions } from 'reducers/PartReducer/partActions';
import { useUser } from 'store/user/useUser';
import { formatPartDescription } from 'utils/helpers/stringFormatter.helpers';
import { PartModalButtonContainer, PartModalSearch } from '../SetModal.styles';
import { PartModalContentType } from './SelectPart';

interface SearchPartProps {
  dispatchNewPart: Dispatch<PartAction>;
  setPartContent: Dispatch<SetStateAction<PartModalContentType>>;
  onAddSelected?: (results: DeviceSearchResponse[]) => void;
  // Selection is owned by the parent so it survives this component unmounting (tick order preserved)
  selectedParts?: DeviceSearchResponse[];
  onToggleSelected?: (part: DeviceSearchResponse) => void;
  onClearSelected?: () => void;
}

// Workflow to search for an existing part
const SearchPart: FC<SearchPartProps> = ({
  dispatchNewPart,
  setPartContent,
  onAddSelected,
  selectedParts = [],
  onToggleSelected,
  onClearSelected,
}) => {
  const [language] = useLanguage();
  const user = useUser();
  // Hook to search for an existing part
  const { query, setQuery, parts, loading } = useQueryParts();

  const isSelected = (code: string): boolean => selectedParts.some(p => p.code === code);
  const toggle = (part: DeviceSearchResponse): void => onToggleSelected?.(part);

  // Updates the new part based on the item selected from search results
  const handleClick = (part: DeviceSearchResponse): void => {
    // Mid multi-select, a click ticks the result; the single-device form would drop the ticked ones
    if (onAddSelected && selectedParts.length > 0) return onToggleSelected?.(part);
    dispatchNewPart(partActions.fromSearch(part));
    setPartContent('add');
  };

  return (
    <Fragment>
      <InputField
        variant="outlined"
        fullWidth
        placeholder={language.AddModal.Device.search}
        InputProps={{
          startAdornment: <SearchOutlined sx={{ color: Theme.greys.GREY2, mr: '1rem' }} />,
        }}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {onAddSelected && selectedParts.length > 0 && (
        // Keeps what was ticked visible while the user searches for more
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', mt: '1rem' }}>
          <span>{language.AddModal.Device.selectedLabel}</span>
          {selectedParts.map(part => (
            <Chip key={part.code} label={formatPartDescription(user, part)} onDelete={() => toggle(part)} />
          ))}
        </Box>
      )}
      <PartModalSearch $loading={loading}>
        {loading ? (
          <PageSpinner />
        ) : (
          parts.map(part => (
            <PartModalButtonContainer
              key={part.code}
              style={onAddSelected && { display: 'flex', alignItems: 'center' }}
            >
              {onAddSelected && (
                <Checkbox
                  checked={isSelected(part.code)}
                  onChange={() => toggle(part)}
                  inputProps={{ 'aria-label': `${language.AddModal.Device.selectResult} ${part.code}` }}
                />
              )}
              <Button fullWidth onClick={() => handleClick(part)}>
                {formatPartDescription(user, part)}
              </Button>
            </PartModalButtonContainer>
          ))
        )}
      </PartModalSearch>
      {onAddSelected && selectedParts.length > 0 && (
        <PartModalButtonContainer style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
          <Button fullWidth onClick={() => onAddSelected(selectedParts)}>
            {`${language.AddModal.Device.addSelected} ${selectedParts.length} ${
              selectedParts.length === 1 ? language.AddModal.Device.device : language.AddModal.Device.devices
            }`}
          </Button>
          <Button onClick={() => onClearSelected?.()}>{language.AddModal.Device.clearSelection}</Button>
        </PartModalButtonContainer>
      )}
    </Fragment>
  );
};

export default SearchPart;
